import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useHistory } from 'react-router-dom';

import { makeStyles } from '@material-ui/core';
import {
  TEACHERS_BULK_UPLOAD_WORK_UPLOAD_AND_QR_URL,
  TEACHER_ASSIGNMENTS_V2_URL,
  TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL,
  TEACHER_ASSIGNMENT_V2_INSIGHTS_URL
} from 'config/urls';
import { debounce, isNil } from 'lodash';
import { useAssignments, useUserSections } from 'sdk';

import { useNewSchoolYearDialog } from 'pages/Teachers/AssignmentsV2/hooks';
import CoachingContent from 'pages/Teachers/Home/components/CoachingContent';
import ColumnContainer from 'pages/Teachers/Home/components/ColumnContainer';
import DashboardHeader from 'pages/Teachers/Home/components/DashboardHeader';
import NavSearchAndFilter from 'pages/Teachers/Home/components/NavSearchAndFilter';
import NavTitle from 'pages/Teachers/Home/components/NavTitle';
import PriorityGradingList from 'pages/Teachers/Home/components/PriorityGradingList';
import StartEmptyBox from 'pages/Teachers/Home/components/StartEmptyBox';
import TommorowFocusContent from 'pages/Teachers/Home/components/TommorowFocusContent';
import { useNavbar } from 'pages/Teachers/shared';
import { getRenderedSectionName } from 'utils/assignments';
import { bootIntercomWidget } from 'utils/integrations/intercom';
import { reverse } from 'utils/urls';

import AssignmentDialogContext from 'components/AssignmentDialogContext';
import UpdateGoogleClassroomRostersDialog from 'components/UpdateGoogleClassroomRostersDialog';
import UserContext from 'components/UserContext';

import { ReactComponent as CoachingIcon } from './assets/coaching-icon.svg';
import { ReactComponent as CoachingSmallIcon } from './assets/coaching-small-icon.svg';
import { ReactComponent as FocusIcon } from './assets/focus-icon.svg';
import { ReactComponent as FocusSmallIcon } from './assets/focus-small-icon.svg';
import { ReactComponent as GradingIcon } from './assets/grading-icon.svg';
import { ReactComponent as StartIcon } from './assets/start-icon.svg';
import styles from './styles.module.scss';

const HOME_PAGE_ENABLED = process.env.REACT_APP_HOME_PAGE_ENABLED === 'true';

const useStyles = makeStyles((theme) => ({
  selectRoot: {
    height: '36px',
    backgroundColor: '#F8F7FF',
    borderRadius: '10px',
    border: '1.5px solid #E0D9F7',
    padding: '4px 12px',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      borderColor: '#D1C4E9',
      backgroundColor: '#F3F0FF'
    },
    '&:before, &:after': {
      display: 'none'
    }
  },
  selectIcon: {
    color: '#6A61A1',
    right: '4px'
  },
  inputText: {
    color: '#6A61A1',
    fontSize: '14px',
    fontWeight: 400,
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  placeholder: {
    color: '#7B74AB',
    opacity: 0.8
  }
}));

const Home = () => {
  const [isLogged, setIsLogged] = useState(false);
  const [sectionValue, setSectionValue] = useState('placeholder');
  const [openSection, setOpenSection] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [assignments, setAssignments] = useState([]);
  const [assignmentsFilter, setAssignmentsFilter] = useState(null);
  const [shouldExpandAssignmentsList, setShouldExpandAssignmentsList] =
    useState(false);

  const history = useHistory();
  const { user } = useContext(UserContext);

  const { data: userSections } = useUserSections({
    params: {}
  });

  const [pagination, setPagination] = useState({
    offset: 0,
    limit: 20
  });

  const [open, setOpen] = useState(false);

  const { setIsAssignmentDialogOpened } = useContext(AssignmentDialogContext);

  const classes = useStyles();

  const filters = useMemo(
    () => ({
      offset: pagination.offset,
      search: assignmentsFilter,
      section: sectionValue !== 'placeholder' ? sectionValue : null
    }),
    [pagination, assignmentsFilter, sectionValue]
  );

  const { data: trackersOverviewData } = useAssignments({
    params: filters
  });

  const {
    isUpdateGoogleClassroomRostersDialogOpened,
    closeUpdateGoogleClassroomRostersDialog,
    goToGoogleAuthScreen
  } = useNewSchoolYearDialog();

  useEffect(() => {
    if (!HOME_PAGE_ENABLED) {
      history.push(TEACHER_ASSIGNMENTS_V2_URL);
    }
  }, [history]);

  useEffect(() => {
    if (!isNil(trackersOverviewData) && shouldExpandAssignmentsList) {
      setAssignments((prev) => [...prev, ...trackersOverviewData.results]);
      setShouldExpandAssignmentsList(false);
    } else if (!isNil(trackersOverviewData) && pagination.offset === 0) {
      setAssignments(trackersOverviewData.results);
    }
  }, [trackersOverviewData, shouldExpandAssignmentsList, pagination.offset]);

  useEffect(() => {
    if (!user) {
      return;
    }
    bootIntercomWidget({
      email: user.email,
      name: user.name
    });
  }, [user]);

  const runSearch = useMemo(
    () =>
      debounce((value) => {
        setAssignmentsFilter(value);
        setPagination({
          offset: 0,
          limit: 20
        });
      }, 300),
    []
  );

  const onChange = useCallback(
    (e) => {
      const value = e.target.value;
      setQuery(value);
      runSearch(value);
      if (value === '') {
        setAssignments([]);
        setOpen(false);
      } else {
        setOpen(true);
      }
    },
    [runSearch]
  );

  const handleSelect = useCallback(
    ({ assignment }) => {
      setQuery('');
      history.push({
        pathname: reverse(
          assignment.is_math_assignment
            ? assignment.submitted === 0
              ? TEACHERS_BULK_UPLOAD_WORK_UPLOAD_AND_QR_URL
              : TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
            : TEACHER_ASSIGNMENT_V2_INSIGHTS_URL,
          {
            trackerInstanceSectionId: assignment.id
          }
        )
      });
      setOpen(false);
    },
    [history]
  );

  const handleScroll = useCallback(
    (e) => {
      const { scrollTop, clientHeight, scrollHeight } = e.target;

      if (
        scrollTop + clientHeight >= scrollHeight - 5 &&
        !isNil(trackersOverviewData) &&
        !isNil(trackersOverviewData.next)
      ) {
        setPagination((prev) => ({
          ...prev,
          offset: trackersOverviewData.offset + trackersOverviewData.limit
        }));
        setShouldExpandAssignmentsList(true);
      }
    },
    [trackersOverviewData]
  );

  const navConfig = useMemo(
    () => ({
      title: <NavTitle />,
      RightSubheader: (
        <NavSearchAndFilter
          query={query}
          onChange={onChange}
          filtersOpen={filtersOpen}
          setFiltersOpen={setFiltersOpen}
          open={open}
          handleScroll={handleScroll}
          assignments={assignments}
          handleSelect={handleSelect}
          openSection={openSection}
          setOpenSection={setOpenSection}
          sectionValue={sectionValue}
          setSectionValue={setSectionValue}
          setPagination={setPagination}
          userSections={userSections}
          classes={classes}
          getRenderedSectionName={getRenderedSectionName}
        />
      ),
      isTransparent: true
    }),
    [
      query,
      onChange,
      filtersOpen,
      open,
      handleScroll,
      assignments,
      handleSelect,
      openSection,
      sectionValue,
      userSections,
      classes
    ]
  );

  useNavbar(navConfig);

  return (
    <>
      <div className={styles.container}>
        <DashboardHeader setIsLogged={setIsLogged} />

        <div className={styles.grid}>
          {/* COLUMN 1: Priority Grading */}
          <ColumnContainer
            disclaimer="START HERE:"
            titleDescription=" Priority Grading"
            smallIcon={<StartIcon />}
            headerRight={
              isLogged ? <span className={styles.countBadge}>14</span> : null
            }
          >
            {isLogged ? (
              <PriorityGradingList />
            ) : (
              <StartEmptyBox
                stepNumber="STEP 1"
                description="Launch your first student mission to see priority grading appear here."
                icon={<GradingIcon />}
                actionLabel="CREATE ASSIGNMENT"
                handleCreateAssignment={() => {
                  setIsAssignmentDialogOpened(true);
                }}
              />
            )}
          </ColumnContainer>

          {/* COLUMN 2: Tomorrow's Focus */}
          <ColumnContainer
            title=""
            titleDescription="Tomorrow's Focus"
            smallIcon={<FocusSmallIcon />}
          >
            {isLogged ? (
              <TommorowFocusContent />
            ) : (
              <StartEmptyBox
                stepNumber="STEP 2"
                description="Reflect on today's data and set goals for your next lesson."
                icon={<FocusIcon />}
              />
            )}
          </ColumnContainer>

          {/* COLUMN 3: PLC / Coaching */}
          <ColumnContainer
            title=""
            titleDescription="Discuss in PLC / Coaching"
            smallIcon={<CoachingSmallIcon />}
          >
            {isLogged ? (
              <CoachingContent />
            ) : (
              <StartEmptyBox
                stepNumber="STEP 3"
                description="Once students submit, we will group them by misconception for your next team meeting."
                icon={<CoachingIcon />}
              />
            )}
          </ColumnContainer>
        </div>
      </div>
      {isUpdateGoogleClassroomRostersDialogOpened && (
        <UpdateGoogleClassroomRostersDialog
          onSync={goToGoogleAuthScreen}
          onClose={closeUpdateGoogleClassroomRostersDialog}
        />
      )}
    </>
  );
};

export default Home;
